import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import {
  Building2,
  Mail,
  Lock,
  User,
  Eye,
  EyeOff,
  ArrowRight,
  Check,
  Phone,
  Sparkles,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { IconInput } from '../../components/ui/icon-input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import {
  FUNDGENIUS_HQ_PRODUCT_NAME,
  FUNDGENIUS_HQ_TAGLINE,
} from '../../utils/funding/constants';
import {
  agencyOwnerNeedsEmailVerification,
  applyPostAuthRedirect,
  getApiUrl,
  getPostSignupRedirectPath,
  getSignupErrorMessage,
  normalizeLoginEmail,
  persistAuthSession,
  clearStaleAuthRedirectStorage,
  sanitizeAuthRedirectUrl,
  FUNDGENIUS_HQ_CLIENTS_PATH,
} from '../../utils/authUtils';

const API = getApiUrl();

const FundGeniusSignup = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading, checkAuth } = useAuth();
  const [formError, setFormError] = useState('');

  useEffect(() => {
    clearStaleAuthRedirectStorage();
  }, []);

  useEffect(() => {
    if (!authLoading && user) {
      const destination = getPostSignupRedirectPath({ user });
      applyPostAuthRedirect(user, navigate, destination);
    }
  }, [authLoading, user, navigate]);

  const [formData, setFormData] = useState({
    agency_name: '',
    owner_name: '',
    owner_email: '',
    phone: '',
    password: '',
    confirm_password: '',
    termsAccepted: false,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (formError) setFormError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');

    if (
      !formData.agency_name ||
      !formData.owner_name ||
      !formData.owner_email ||
      !formData.password ||
      !formData.phone
    ) {
      const message = 'Please fill in all required fields';
      setFormError(message);
      toast.error(message);
      return;
    }

    if (!formData.termsAccepted) {
      const message = 'Please accept the Terms of Service and Privacy Policy';
      setFormError(message);
      toast.error(message);
      return;
    }

    if (formData.password !== formData.confirm_password) {
      const message = 'Passwords do not match';
      setFormError(message);
      toast.error(message);
      return;
    }

    if (formData.password.length < 8) {
      const message = 'Password must be at least 8 characters';
      setFormError(message);
      toast.error(message);
      return;
    }

    setLoading(true);
    try {
      const payload = {
        email: normalizeLoginEmail(formData.owner_email),
        password: formData.password,
        name: formData.owner_name.trim(),
        agency_name: formData.agency_name.trim(),
        first_name: formData.owner_name.trim().split(' ')[0] || undefined,
        agency_type: 'fundgenius',
      };

      const response = await axios.post(`${API}/auth/signup/agency`, payload, {
        withCredentials: true,
      });

      const { token, user: userData, message, agency_id: agencyId, redirect_url: redirectUrl } = response.data;
      if (token) {
        persistAuthSession({ token, user: userData });
        await checkAuth();
      }

      toast.success(message || 'Account created. Check your email for verification.');

      const signedInUser = userData || {};
      const defaultDestination = getPostSignupRedirectPath({
        user: signedInUser,
        agencyType: signedInUser.agency_type,
        portalType: signedInUser.portal_type,
      });
      const destination =
        sanitizeAuthRedirectUrl(redirectUrl) || defaultDestination || FUNDGENIUS_HQ_CLIENTS_PATH;

      if (agencyOwnerNeedsEmailVerification(signedInUser)) {
        navigate('/verify-email', {
          replace: true,
          state: {
            email: signedInUser.email || payload.email,
            role: 'agency_owner',
            redirectTo: destination,
            agencyId: signedInUser.agency_id || agencyId,
          },
        });
        return;
      }

      applyPostAuthRedirect(signedInUser, navigate, destination);
    } catch (error) {
      const message = getSignupErrorMessage(error);
      setFormError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const features = [
    'Funding intake through funded pipeline',
    'AI advisor recommendations for clients',
    'Lender match and submission tracking',
    'Agency-scoped workspace security',
  ];

  return (
    <div className="min-h-screen bg-[#07111f] text-white flex" data-testid="fundgenius-signup-page">
      <div className="hidden lg:flex lg:w-1/2 p-12 flex-col justify-between relative overflow-hidden border-r border-white/10">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(34,211,238,0.18),transparent_40%),radial-gradient(circle_at_bottom_right,rgba(59,130,246,0.18),transparent_35%)]" />

        <div className="relative">
          <div className="flex items-center gap-3 mb-8">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-cyan-400 to-blue-600 text-sm font-bold">
              FG
            </div>
            <div>
              <h1 className="text-2xl font-bold">{FUNDGENIUS_HQ_PRODUCT_NAME}</h1>
              <p className="text-sm text-slate-400">Funding operations workspace</p>
            </div>
          </div>
        </div>

        <div className="relative">
          <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-cyan-400/20 bg-cyan-400/10 px-3 py-1 text-xs font-medium text-cyan-200">
            <Sparkles className="h-3.5 w-3.5" />
            Agency workspace signup
          </div>
          <h2 className="text-4xl font-bold leading-tight mb-6">
            Launch your <br />
            funding operations portal
          </h2>
          <p className="text-lg text-slate-300 max-w-md mb-8">{FUNDGENIUS_HQ_TAGLINE}</p>
          <ul className="space-y-3">
            {features.map((feature) => (
              <li key={feature} className="flex items-center gap-3 text-slate-200">
                <Check size={18} className="text-cyan-300 shrink-0" />
                <span>{feature}</span>
              </li>
            ))}
          </ul>
        </div>

        <p className="relative text-sm text-slate-500">
          Already have an account?{' '}
          <Link to="/login" className="text-cyan-300 hover:text-cyan-200 font-medium">
            Sign in
          </Link>
        </p>
      </div>

      <div className="flex-1 flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">
          <div className="mb-8 lg:hidden">
            <div className="flex items-center gap-3 mb-2">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-cyan-400 to-blue-600 text-sm font-bold">
                FG
              </div>
              <div>
                <h1 className="text-xl font-bold">{FUNDGENIUS_HQ_PRODUCT_NAME}</h1>
                <p className="text-xs text-slate-400">Create your agency workspace</p>
              </div>
            </div>
          </div>

          <h2 className="text-2xl font-bold mb-2">Create your FundGenius HQ account</h2>
          <p className="text-slate-400 mb-8">
            Register your agency to access the funding workspace at app.fundgeniushq.com.
          </p>

          {formError ? (
            <div
              className="mb-6 rounded-xl border border-red-400/30 bg-red-500/10 px-4 py-3 text-sm text-red-100"
              data-testid="signup-form-error"
              role="alert"
            >
              {formError}
            </div>
          ) : null}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label htmlFor="agency_name" className="text-slate-200">Agency / business name</Label>
              <IconInput
                id="agency_name"
                name="agency_name"
                icon={Building2}
                placeholder="Acme Funding Group"
                value={formData.agency_name}
                onChange={handleChange}
                className="mt-1.5 bg-white/5 border-white/10 text-white placeholder:text-slate-500"
              />
            </div>

            <div>
              <Label htmlFor="owner_name" className="text-slate-200">Your name</Label>
              <IconInput
                id="owner_name"
                name="owner_name"
                icon={User}
                placeholder="Jordan Lee"
                value={formData.owner_name}
                onChange={handleChange}
                className="mt-1.5 bg-white/5 border-white/10 text-white placeholder:text-slate-500"
              />
            </div>

            <div>
              <Label htmlFor="owner_email" className="text-slate-200">Work email</Label>
              <IconInput
                id="owner_email"
                name="owner_email"
                type="email"
                icon={Mail}
                placeholder="you@company.com"
                value={formData.owner_email}
                onChange={handleChange}
                className="mt-1.5 bg-white/5 border-white/10 text-white placeholder:text-slate-500"
              />
            </div>

            <div>
              <Label htmlFor="phone" className="text-slate-200">Phone</Label>
              <IconInput
                id="phone"
                name="phone"
                icon={Phone}
                placeholder="(555) 555-0100"
                value={formData.phone}
                onChange={handleChange}
                className="mt-1.5 bg-white/5 border-white/10 text-white placeholder:text-slate-500"
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-slate-200">Password</Label>
              <div className="relative mt-1.5">
                <IconInput
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  icon={Lock}
                  placeholder="Minimum 8 characters"
                  value={formData.password}
                  onChange={handleChange}
                  className="bg-white/5 border-white/10 text-white placeholder:text-slate-500"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-200"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <div>
              <Label htmlFor="confirm_password" className="text-slate-200">Confirm password</Label>
              <IconInput
                id="confirm_password"
                name="confirm_password"
                type={showPassword ? 'text' : 'password'}
                icon={Lock}
                placeholder="Repeat password"
                value={formData.confirm_password}
                onChange={handleChange}
                className="mt-1.5 bg-white/5 border-white/10 text-white placeholder:text-slate-500"
              />
            </div>

            <label className="flex items-start gap-3 text-sm text-slate-300">
              <input
                type="checkbox"
                name="termsAccepted"
                checked={formData.termsAccepted}
                onChange={(e) => {
                  setFormData({ ...formData, termsAccepted: e.target.checked });
                  if (formError) setFormError('');
                }}
                className="mt-1"
              />
              <span>
                I agree to the Terms of Service and Privacy Policy.
              </span>
            </label>

            <Button
              type="submit"
              disabled={loading}
              className="w-full rounded-full bg-cyan-500 text-slate-950 hover:bg-cyan-400"
            >
              {loading ? 'Creating account...' : 'Create FundGenius HQ account'}
              {!loading ? <ArrowRight className="ml-2 h-4 w-4" /> : null}
            </Button>
          </form>

          <p className="mt-6 text-center text-sm text-slate-400">
            Already have an account?{' '}
            <Link to="/login" className="text-cyan-300 hover:text-cyan-200 font-medium">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default FundGeniusSignup;
